import PropTypes from "prop-types";

// material-ui
import {
  Button,
  Grid,
  InputAdornment,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";

// third-party
import { useTable, useSortBy, usePagination } from "react-table";

// project import
import React, { useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import { TableHead } from "@mui/material";
import MainCard from "../MainCard";
import { HeaderSort } from "./ReactTable";
import { IconSearch } from "@tabler/icons-react";
import { useForm, FormProvider } from "react-hook-form";
import RHFTextField from "../RHFComponents/RHFTextField";

const StyleTableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.cardBackground,
  overflow: "auto",
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: "14px",
  fontWeight: "500",
  textTransform: "capitalize",
}));

// material-ui

const ScrollX = styled("div")({
  width: "100%",
  overflowX: "auto",
  display: "block",
});

// Component for the nested table inside expanded rows
const ExpandedRowTable = ({
  columnsExpandableTable,
  expandableTableData,
  //   setSortStateExpanded,
}) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns: columnsExpandableTable,
      data: expandableTableData,
    });

  return (
    <Table {...getTableProps()} style={{ marginTop: "10px" }}>
      <StyleTableHeader>
        {headerGroups.map((headerGroup, i) => (
          <TableRow key={i} {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, index) => (
              <TableHeaderCell
                key={index}
                {...column.getHeaderProps([{ className: column.className }])}
              >
                <HeaderSort column={column} sort={false} />
              </TableHeaderCell>
            ))}
          </TableRow>
        ))}
      </StyleTableHeader>
      <TableBody {...getTableBodyProps()}>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={columnsExpandableTable.length}
              style={{ textAlign: "center", padding: "20px" }}
            >
              No Data Found
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, i) => {
            prepareRow(row);
            return (
              <TableRow key={i} {...row.getRowProps()}>
                {row.cells.map((cell, index) => (
                  <TableCell key={index} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
};

ExpandedRowTable.propTypes = {
  columnsExpandableTable: PropTypes.object,
  expandableTableData: PropTypes.array,
  expandableTableCount: PropTypes.number,
  setSortStateExpanded: PropTypes.func,
  intl: PropTypes.func,
};

// ==============================|| REACT TABLE ||============================== //

const PaginatedTable = ({
  columns,
  data,
  heading,
  top = false,
  paginated,
  pageOffset,
  limit = 10,
  setPageOffsetFromParents,
  totalCount,
  headingButtonText,
  headingBtnClick,
  expandedRows,
  columnsExpandableTable,
  expandableTableData,
  expandableTableCount,
  expandableTableLoading,
  setSortStateExpanded,
  hideFeatureButton,
  searchText,
  setSearchText,
  showSearch = false,
}) => {
  const methods = useForm({
    defaultValues: {
      search: searchText || "",
    },
  });

  const { watch } = methods;
  const watchedSearch = watch("search");

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (setSearchText) setSearchText(watchedSearch);
    }, 250); // Debounce to reduce frequency of API/search calls

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedSearch]);

  const pageCountValue = useMemo(
    () => (totalCount ? Math.ceil(totalCount / limit) : 0),
    [totalCount, limit]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: pageOffset ?? 0, pageSize: limit ?? 10 },
      manualPagination: true,
      pageCount: pageCountValue,
    },
    useSortBy,
    usePagination
  );

  const handleChangePagination = (event, value) => {
    gotoPage(value - 1);
    setPageOffsetFromParents(value - 1);
  };

  return (
    <FormProvider {...methods}>
      <MainCard
        title={heading}
        content={false}
        secondary={
          !hideFeatureButton && (
            <Button variant="outlined" onClick={headingBtnClick}>
              {`${headingButtonText}`}
            </Button>
          )
        }
        divider
        sx={{
          "& .MuiCardHeader-root.MuiCardHeader-root": {
            width: "100% !important",
            padding: (theme) => theme.spacing(0, 2),
            overflow: "auto",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
          },
        }}
      >
        <ScrollX>
          {showSearch && (
            <Grid container spacing={2} alignItems="center" sx={{ p: 2 }}>
              <Grid item xs={12} sm={3}>
                <RHFTextField
                  name="search"
                  placeholder="Search..."
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconSearch />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
          <Table {...getTableProps()}>
            <StyleTableHeader
              sx={{ borderTopLeftRadius: 5, borderTopRightRadius: 5 }}
            >
              {headerGroups.map((headerGroup, i) => (
                <TableRow key={i} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) => (
                    <TableHeaderCell
                      key={index}
                      {...column.getHeaderProps([
                        { className: column.className },
                      ])}
                    >
                      <HeaderSort column={column} sort={false} />
                    </TableHeaderCell>
                  ))}
                </TableRow>
              ))}
            </StyleTableHeader>
            <TableBody
              {...getTableBodyProps()}
              style={{ position: "relative" }}
            >
              {!data.length ? (
                <TableRow>
                  <TableCell>
                    <span style={{ position: "absolute", left: "50%" }}>
                      No data found
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                page.map((row, i) => {
                  prepareRow(row);
                  return (
                    <React.Fragment key={i}>
                      <TableRow {...row.getRowProps()}>
                        {row.cells.map((cell, index) => (
                          <TableCell
                            key={index}
                            {...cell.getCellProps([
                              { className: cell.column.className },
                            ])}
                          >
                            {cell.render("Cell")}
                          </TableCell>
                        ))}
                      </TableRow>
                      {/* Expanded row */}
                      {expandedRows?.[row?.original?.uuid] &&
                        !expandableTableLoading && (
                          <TableRow>
                            <TableCell colSpan={columns.length}>
                              <ExpandedRowTable
                                columnsExpandableTable={columnsExpandableTable}
                                expandableTableData={expandableTableData}
                                expandableTableCount={expandableTableCount}
                                setSortStateExpanded={setSortStateExpanded}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                    </React.Fragment>
                  );
                })
              )}
              {paginated && !top && (
                <TableRow>
                  <TableCell sx={{ p: 2 }} colSpan={7}>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: "auto" }}
                    >
                      <Grid item></Grid>
                      <Grid item sx={{ mt: { xs: 2, sm: 0 } }}>
                        <Pagination
                          count={pageCountValue}
                          page={pageIndex + 1}
                          onChange={handleChangePagination}
                          color="primary"
                          variant="combined"
                        />
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollX>
      </MainCard>
    </FormProvider>
  );
};

export default PaginatedTable;

PaginatedTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  heading: PropTypes.string.isRequired,
  top: PropTypes.bool,
  paginated: PropTypes.bool,
  pageOffset: PropTypes.number,
  limit: PropTypes.number,
  setPageOffsetFromParents: PropTypes.func.isRequired,
  totalCount: PropTypes.number,
  headingButtonText: PropTypes.string,
  headingBtnClick: PropTypes.func,
  expandedRows: PropTypes.object,
  columnsExpandableTable: PropTypes.object,
  expandableTableData: PropTypes.array,
  expandableTableCount: PropTypes.number,
  expandableTableLoading: PropTypes.bool,
  setSortStateExpanded: PropTypes.func,
  hideFeatureButton: PropTypes.bool,
  showSearch: PropTypes.bool,
  searchText: PropTypes.string,
  setSearchText: PropTypes.func,
};
